import axios, {
  type AxiosPromise,
  type AxiosRequestConfig,
  type CancelToken,
} from 'axios';

import { sendGtagEventApiData } from './gtagEvents';
import * as logging from './logging';
import {
  UNEXPECTED_RESPONSE_FINGERPRINT,
  UnexpectedResponseError,
} from './unexpectedResponse';
import { keysToLowerCase } from './utils';

// How a web page starts, unlike the XML that some services send without saying
const pageStart = /^\s*<(!doctype\s+html|html|head|body|title|meta|script)\b/i;

axios.interceptors.response.use((response) => {
  if (
    response.data &&
    response.status >= 200 &&
    response.status < 300 &&
    typeof response.data === 'string'
  ) {
    // Need to lowercase headers as axios doesn't do this before the interceptor.
    const contentType: string | undefined = keysToLowerCase(response?.headers)[
      'content-type'
    ];
    const claimsJSON = contentType?.includes('json');
    // JSON was asked for and a web page came back: whatever answered, it wasn't
    // the API. Not all of them say it's a page, some send it as plain text or
    // with no content type at all
    const isPageInstead =
      response.config.responseType === 'json' &&
      (contentType?.includes('html') ||
        ((!contentType || contentType.includes('text/plain')) &&
          pageStart.test(response.data)));
    if (claimsJSON || isPageInstead) {
      try {
        response.data = JSON.parse(response.data);
      } catch (error) {
        const unexpected = new UnexpectedResponseError(
          error instanceof Error ? error.message : String(error),
          {
            url: response.config.url,
            status: response.status,
            contentType,
            body: response.data,
          }
        );
        // Counts affected users, including those who never write in. Only the
        // title, not the snippet: a portal or block page can show the user's
        // login, device, IP or a token, which shouldn't silently reach Sentry
        logging.error(unexpected, {
          tags: { origin: 'fetchData', contentType },
          extra: { url: unexpected.url, title: unexpected.title },
          fingerprint: [UNEXPECTED_RESPONSE_FINGERPRINT],
        });
        throw unexpected;
      }
    }
  }
  return response;
});

export default function fetchData<T>(
  url: string,
  cancelToken: CancelToken | undefined = undefined,
  axiosOptions: AxiosRequestConfig = {}
): AxiosPromise<T> {
  const options = { ...axiosOptions };
  if (!options.responseType) {
    options.responseType =
      typeof axiosOptions?.headers?.Accept === 'string' &&
      !axiosOptions.headers.Accept.includes('json')
        ? 'text'
        : 'json';
  }
  if (!options?.headers) {
    options.headers = {};
  }
  if (!options.headers.Accept) {
    options.headers = { ...options.headers, Accept: 'application/json' };
  }
  const promise = axios({
    url,
    method: 'GET',
    cancelToken,
    ...options,
  });

  promise.then(
    () => {
      sendGtagEventApiData('success', url);
    },
    () => {
      sendGtagEventApiData('fail', url);
    }
  );

  return promise;
}
